/**
 * NIF-1101: copy enseñable MatchData desde keys del back → i18n admin.
 * El caller solo invoca esto si `error_code === 2014` (no se identifica por texto).
 * Prioridad: `match_data_errors` → JSON embebido en `error.msg`.
 */

const KEY_ALIASES: Record<string, string> = {
	name: 'names',
	first_name: 'names',
	surname: 'surnames',
	last_name: 'surnames',
	id_document: 'identifier',
	document_number: 'identifier',
	birth_date: 'birthdate',
	zip_code: 'postal_code',
};

const DOCUMENT_NUMBER_KEYS = new Set(['identifier', 'nif', 'nie', 'cif']);

export type MatchDataErrorKeyRow = { key?: string | null };

export type MatchDataKoErrorPayload = {
	match_data_errors?: MatchDataErrorKeyRow[] | null;
	/** Msg técnico del Action KO (puede embeber el JSON con `key` si es MatchData). */
	msg?: string | null;
};

export function canonicalizeMatchDataErrorKey(raw: string): string {
	const trimmed = raw.trim();
	return KEY_ALIASES[trimmed] ?? trimmed;
}

/** Claves canónicas únicas en orden de aparición. */
export function uniqueMatchDataErrorKeys(errors: MatchDataErrorKeyRow[] | null | undefined): string[] {
	if (!Array.isArray(errors) || errors.length === 0) {
		return [];
	}
	const keys: string[] = [];
	for (const row of errors) {
		const raw = typeof row?.key === 'string' ? row.key : '';
		if (!raw.trim()) {
			continue;
		}
		const canonical = canonicalizeMatchDataErrorKey(raw);
		if (!keys.includes(canonical)) {
			keys.push(canonical);
		}
	}
	return keys;
}

/**
 * Extrae el array/objeto JSON del msg técnico (legado sin `match_data_errors`).
 * No depende del prefijo textual: busca el primer `[` o `{`.
 * Solo usar tras comprobar `error_code === 2014`.
 */
export function parseMatchDataErrorKeysFromTechnicalMsg(msg: string | null | undefined): MatchDataErrorKeyRow[] {
	if (typeof msg !== 'string' || !msg.trim()) {
		return [];
	}
	const trimmed = msg.trim();
	const arrayIdx = trimmed.indexOf('[');
	const objectIdx = trimmed.indexOf('{');
	let start = -1;
	if (arrayIdx >= 0 && objectIdx >= 0) {
		start = Math.min(arrayIdx, objectIdx);
	} else {
		start = arrayIdx >= 0 ? arrayIdx : objectIdx;
	}
	if (start < 0) {
		return [];
	}
	const jsonPart = trimmed.slice(start);
	try {
		const decoded = JSON.parse(jsonPart) as unknown;
		const rows = Array.isArray(decoded)
			? decoded
			: decoded && typeof decoded === 'object' && 'key' in (decoded as object)
				? [decoded]
				: [];
		return uniqueMatchDataErrorKeys(
			rows.filter((row): row is MatchDataErrorKeyRow => !!row && typeof row === 'object'),
		).map((key) => ({ key }));
	} catch {
		return [];
	}
}

/**
 * Resuelve mensaje de usuario/admin para campos KO.
 * @returns null si no hay keys → el caller usa catálogo / TITLE_2014.
 */
export function resolveMatchDataErrorsUserMessage(
	errors: MatchDataErrorKeyRow[] | null | undefined,
	instant: (key: string, params?: Record<string, string>) => string,
	hasTranslation?: (key: string) => boolean,
): string | null {
	const keys = uniqueMatchDataErrorKeys(errors);
	if (keys.length === 0) {
		return null;
	}

	// No filtrar con hasTranslation: esas keys viven en i18n estático; MasterService
	// a menudo no las conoce y bloquearía el copy de campo (NIF-1101).
	void hasTranslation;

	if (keys.length === 1) {
		const key = keys[0];
		const fieldKey = DOCUMENT_NUMBER_KEYS.has(key)
			? 'ERRORS.PROCESS.MATCH_DATA_FIELD.identifier'
			: `ERRORS.PROCESS.MATCH_DATA_FIELD.${key}`;
		const specific = instant(fieldKey);
		if (specific && specific !== fieldKey) {
			return specific;
		}
		const labelKey = `ERRORS.PROCESS.MATCH_DATA_FIELD_LABEL.${key}`;
		const label = instant(labelKey);
		const labelText = label && label !== labelKey ? label : key;
		return instant('ERRORS.PROCESS.MATCH_DATA_FIELD.generic', { field: labelText });
	}

	const labels = keys.map((key) => {
		const labelKey = `ERRORS.PROCESS.MATCH_DATA_FIELD_LABEL.${key}`;
		const label = instant(labelKey);
		return label && label !== labelKey ? label : key;
	});
	return instant('ERRORS.PROCESS.MATCH_DATA_MULTI', { fields: labels.join(', ') });
}

/** NIF-1101: keys estructuradas o parse desde `msg` técnico. */
export function resolveMatchDataUserMessageFromKoError(
	payload: MatchDataKoErrorPayload | null | undefined,
	instant: (key: string, params?: Record<string, string>) => string,
	hasTranslation?: (key: string) => boolean,
): string | null {
	const fromPayload = resolveMatchDataErrorsUserMessage(payload?.match_data_errors, instant, hasTranslation);
	if (fromPayload) {
		return fromPayload;
	}
	const fromMsg = parseMatchDataErrorKeysFromTechnicalMsg(payload?.msg ?? null);
	return resolveMatchDataErrorsUserMessage(fromMsg, instant, hasTranslation);
}
